import type { Step } from 'react-joyride';
import { StepContent } from '@/context/OnboardingGuideContext/ui/StepContent';
import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/styles/colors';
import { OnboardingGuideStep } from './ui/OnboardingGuideStep';
import { ValueOf } from '@/types/sharedTypes';
import { delay } from '@/helpers/helpers';
import { getTransactionsPath } from '@/bundle/_OrgAdmin/path/path';
import { getTransactionDetailsPath } from '@/bundle/_OrgAdmin/pages/TransactionDetailsPage/path/path';

export const ONBOARDING_GUIDE_FINISHED_STORAGE_KEY = 'ONBOARDING_GUIDE_FINISHED';
export const ONBOARDING_GUIDE_LAST_STEP_INDEX_STORAGE_KEY = 'ONBOARDING_GUIDE_LAST_STEP_INDEX';

export const ONBOARDING_GUIDE_STEP_ID = {
  DEPOSIT_ACCOUNTS: 'deposit-accounts',
  CREATE_TRANSACTION: 'create-transaction',
  CREATE_WIRE: 'create-wire',
  ASSIGN_RECIPIENT: 'assign-recipient',
} as const;

export type OnboardingGuideStepIdType = ValueOf<typeof ONBOARDING_GUIDE_STEP_ID>;

export const ONBOARDING_GUIDE_STEPS: Step[] = [
  {
    target: `#${ONBOARDING_GUIDE_STEP_ID.DEPOSIT_ACCOUNTS}`,
    id: ONBOARDING_GUIDE_STEP_ID.DEPOSIT_ACCOUNTS,
    title: 'Add Your Receiving Account',
    content: (
      <StepContent>
        <Typography variant='bodySmall' color={COLORS.grey[300]}>
          Start by adding your banking information so you can securely share receiving instructions in future
          transactions.
        </Typography>
      </StepContent>
    ),
    tooltipComponent: OnboardingGuideStep,
    placement: 'auto',
    skipScroll: true,
    skipBeacon: true,
    locale: {
      close: 'Next',
    },
  },
  {
    target: `#${ONBOARDING_GUIDE_STEP_ID.CREATE_TRANSACTION}`,
    id: ONBOARDING_GUIDE_STEP_ID.CREATE_TRANSACTION,
    title: 'Collect or Share Wire Instructions',
    content: (
      <StepContent>
        <Typography variant='bodySmall' color={COLORS.grey[300]}>
          Create a transaction to securely collect wire instructions from recipients or share receiving information with
          others.
        </Typography>
      </StepContent>
    ),
    tooltipComponent: OnboardingGuideStep,
    placement: 'auto',
    skipBeacon: true,
    skipScroll: true,
    data: {
      previousPath: getTransactionsPath,
    },
    locale: {
      close: 'Skip',
    },
  },
  {
    target: `#${ONBOARDING_GUIDE_STEP_ID.CREATE_WIRE}`,
    id: ONBOARDING_GUIDE_STEP_ID.CREATE_WIRE,
    title: 'Create Your First Wire',
    content: (
      <StepContent>
        <Typography variant='bodySmall' color={COLORS.grey[300]}>
          Choose the appropriate wire type to get started:
          <br />
          <b>Sending:</b> select this option if you need to collect wire instructions from another party.
          <br />
          <b>Receiving:</b> select this option if you need to share your own wire instructions with another party.
        </Typography>
      </StepContent>
    ),
    tooltipComponent: OnboardingGuideStep,
    placement: 'auto',
    skipScroll: true,
    skipBeacon: true,
    before: () => {
      return delay(1000);
    },
    data: {
      previousPath: getTransactionsPath,
    },
    locale: {
      close: 'Skip',
    },
  },
  {
    target: `#${ONBOARDING_GUIDE_STEP_ID.ASSIGN_RECIPIENT}`,
    id: ONBOARDING_GUIDE_STEP_ID.ASSIGN_RECIPIENT,
    title: 'Assign Recipient',
    content: (
      <StepContent>
        <Typography variant='bodySmall' color={COLORS.grey[300]}>
          Once the recipient’s information is complete, click Assign to begin collecting or sharing wire instructions.
          Enable ID Verification if you’d like to verify the recipient’s identity before they submit wire information.
        </Typography>
      </StepContent>
    ),
    tooltipComponent: OnboardingGuideStep,
    placement: 'auto',
    blockTargetInteraction: true,
    skipBeacon: true,
    data: {
      previousPath: getTransactionDetailsPath,
    },
    locale: {
      close: 'Finish',
    },
  },
];
